import React, { useState, useEffect, useMemo, useCallback, memo } from "react";
import { Form, Input, Select, Flex } from "antd";
import {
  PaperClipOutlined,
  PictureOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { useSelector, useDispatch } from "react-redux";
import { FetchPlaceholderFields } from "../../../assets/api/ScriptsService";
import { fetchUserFilters } from "../../../store/filters";
import {
  getTemplateComponents,
  AddTemplate,
} from "../../../assets/api/templateService";
import { useNavigate } from "react-router-dom";
import CustomNotification from "../../../Reuseable/CustomNotification/CustomNotification";
import NormalButton from "../../../Reuseable/NormalButton/NormalButton";
import "./EmailCreateTemplate.scss";

const { TextArea } = Input;
const { Option } = Select;

const EmailCreateTemplate = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [subjectText, setSubjectText] = useState("");
  const [bodyText, setBodyText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubjectChange = useCallback((e) => {
    setSubjectText(e.target.value);
  }, []);

  const handleBodyChange = useCallback((e) => {
    setBodyText(e.target.value);
  }, []);

  const token = useSelector((state) => state.auth.token);
  const dispatch = useDispatch();
  const { filter_list } = useSelector((state) => state.filters) || {};

  useEffect(() => {
    dispatch(fetchUserFilters());
  }, [dispatch]);

  const dynamicPortfoliosOptions = useMemo(() => {
    const portfoliosData =
      filter_list?.find((item) => item?.name === "portfolios")?.filters || [];
    return portfoliosData.map((d) => ({
      value: d.id || d,
      label: d.name || d.key || d,
    }));
  }, [filter_list]);

  const [placeholderFields, setPlaceholderFields] = useState([]);
  const [loadingFields, setLoadingFields] = useState(false);
  const [selectedBodyPlaceholder, setSelectedBodyPlaceholder] = useState(null);

  const [languageOptions, setLanguageOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [loadingComponents, setLoadingComponents] = useState(false);

  useEffect(() => {
    if (token) {
      setLoadingComponents(true);
      getTemplateComponents(token)
        .then((res) => {
          if (res?.data?.data) {
            const { languages, categories } = res.data.data;
            if (languages) {
              setLanguageOptions(
                languages.map((lang) => ({
                  label: lang.label,
                  value: lang.code,
                })),
              );
            }
            if (categories) {
              setCategoryOptions(
                categories.map((cat) => ({
                  label: cat.label,
                  value: cat.value,
                })),
              );
            }
          }
        })
        .catch((err) =>
          console.error("Error fetching template components:", err),
        )
        .finally(() => setLoadingComponents(false));
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      setLoadingFields(true);
      FetchPlaceholderFields(token)
        .then((res) => {
          let dataArray = [];
          if (
            res?.data?.data?.field_names &&
            Array.isArray(res.data.data.field_names)
          ) {
            dataArray = res.data.data.field_names;
          } else if (
            res?.data?.field_names &&
            Array.isArray(res.data.field_names)
          ) {
            dataArray = res.data.field_names;
          } else if (Array.isArray(res?.data)) {
            dataArray = res.data;
          } else if (Array.isArray(res?.data?.data)) {
            dataArray = res.data.data;
          }

          const formatLabel = (str) => {
            if (!str) return "";
            return str
              .split("_")
              .map(
                (word) =>
                  word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
              )
              .join(" ");
          };

          const options = dataArray.map((item) => {
            const strVal =
              typeof item === "string"
                ? item
                : item?.name || item?.key || String(item);
            return { value: strVal, label: formatLabel(strVal) };
          });

          setPlaceholderFields(options);
        })
        .catch((err) => console.error("Error fetching placeholders:", err))
        .finally(() => setLoadingFields(false));
    }
  }, [token]);

  const handleBodyPlaceholderSelect = useCallback(
    (val) => {
      if (!val) return;
      const currentBody = form.getFieldValue("body") || "";
      const placeholderText = `{{xbucketdata.${val}}}`;
      if (currentBody.length + placeholderText.length > 2048) {
        CustomNotification.show(
          "Placeholder cannot be added as it exceeds the 2048 characters limit.",
          "error",
        );
        setSelectedBodyPlaceholder(null);
        return;
      }
      const newBody = currentBody + placeholderText;
      form.setFieldsValue({ body: newBody });
      setBodyText(newBody);
      setSelectedBodyPlaceholder(null);
    },
    [form],
  );

  const onFinish = useCallback(
    async (values) => {
      try {
        setSubmitting(true);

        const components = [];

        if (values.body) {
          components.push({
            type: "BODY",
            text: values.body,
          });
        }

        const payload = {
          portfolio_id: Number(values.portfolio),
          channel: "email",
          name: values.name,
          language: values.language,
          category: values.category,
          subject: values.subject,
          components,
        };

        const res = await AddTemplate(token, payload);
        if (res.status === 201 || res.status === 200) {
          CustomNotification.show("Template created successfully", "success");
          navigate("/communication/email/template");
        }
      } catch (err) {
        CustomNotification.show(err || "Failed to create template", "error");
      } finally {
        setSubmitting(false);
      }
    },
    [token, navigate],
  );

  const handleBackClick = useCallback(() => {
    navigate("/communication/email/template");
  }, [navigate]);

  return (
    <div className="email-create-template-container">
      <Flex
        wrap="wrap"
        gap={24}
        className="email-create-template-flex-layout"
        style={{ height: "100%" }}
      >
        {/* Left Section - Form */}
        <div className="email-left-section">
          <div className="emailCreateTemplateMain">
            <div className="emailCreateTemplateHeader">
              <h2>Create Email Template</h2>
            </div>

            <Form form={form} layout="vertical" onFinish={onFinish}>
              <Flex
                wrap="wrap"
                gap={16}
                className="emailCreateTemplateFormWrapper"
              >
                <Form.Item
                  label="Name"
                  name="name"
                  rules={[
                    { required: true, message: "Please enter template name" },
                  ]}
                >
                  <Input placeholder="Enter Name" />
                </Form.Item>
                <Form.Item
                  label="Language"
                  name="language"
                  rules={[
                    { required: true, message: "Please select language" },
                  ]}
                >
                  <Select
                    placeholder="Select Language"
                    options={languageOptions}
                    loading={loadingComponents}
                    allowClear
                  />
                </Form.Item>
                <Form.Item
                  label="Portfolio"
                  name="portfolio"
                  rules={[
                    { required: true, message: "Please select portfolio" },
                  ]}
                >
                  <Select
                    placeholder="Select Portfolio"
                    options={dynamicPortfoliosOptions}
                    allowClear
                  />
                </Form.Item>
                <Form.Item
                  label="Category"
                  name="category"
                  rules={[
                    { required: true, message: "Please select category" },
                  ]}
                >
                  <Select
                    placeholder="Select Category"
                    options={categoryOptions}
                    loading={loadingComponents}
                    allowClear
                  />
                </Form.Item>
                <Form.Item
                  label="Template Type"
                  name="templateType"
                  initialValue="default"
                  rules={[
                    { required: true, message: "Please select template type" },
                  ]}
                >
                  <Select placeholder="Default">
                    <Option value="default">Default</Option>
                  </Select>
                </Form.Item>
              </Flex>

              <div className="emailCreateTemplateFieldsWrapper">
                <Form.Item
                  label="Subject"
                  name="subject"
                  rules={[{ required: true, message: "Please enter subject" }]}
                >
                  <Input
                    placeholder="Enter email subject"
                    onChange={handleSubjectChange}
                  />
                </Form.Item>

                <div
                  className="email-custom-body-section"
                  style={{ marginBottom: "16px" }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "8px",
                    }}
                  >
                    <div className="ant-form-item-label" style={{ padding: 0 }}>
                      <label
                        title="Body"
                        className="ant-form-item-required"
                        style={{ color: "#212330" }}
                      >
                        Body
                      </label>
                    </div>
                    <div style={{ display: "flex", gap: "8px" }}>
                      <Select
                        showSearch
                        placeholder="Select Placeholder"
                        style={{ width: "160px" }}
                        options={placeholderFields}
                        loading={loadingFields}
                        value={selectedBodyPlaceholder}
                        onChange={handleBodyPlaceholderSelect}
                        allowClear
                        filterOption={(input, option) =>
                          (option?.label ?? "")
                            .toLowerCase()
                            .includes(input.toLowerCase())
                        }
                      />
                    </div>
                  </div>
                  <Form.Item
                    name="body"
                    style={{ marginBottom: 0 }}
                    rules={[
                      { required: true, message: "Please enter body text" },
                    ]}
                  >
                    <TextArea
                      rows={6}
                      maxLength={2048}
                      onChange={handleBodyChange}
                      placeholder="Enter email body (HTML or plain text supported)"
                    />
                  </Form.Item>
                  <div
                    className="char-count"
                    style={{
                      textAlign: "right",
                      fontSize: "12px",
                      color: "#888",
                      marginTop: "4px",
                    }}
                  >
                    {bodyText.length}/2048
                  </div>
                </div>
              </div>

              <Flex
                wrap="wrap"
                gap={12}
                className="emailCreateTemplateSubmitAction"
                style={{ marginTop: "24px", width: "100%" }}
              >
                <NormalButton
                  type="default"
                  onClick={handleBackClick}
                  className="emailCreateTemplateCancelBtn"
                  width="95px"
                >
                  Back
                </NormalButton>
                <NormalButton
                  type="primary"
                  htmlType="submit"
                  loading={submitting}
                  className="emailCreateTemplateSubmitBtn"
                  width="95px"
                >
                  Create
                </NormalButton>
              </Flex>
            </Form>
          </div>
        </div>

        {/* Right Section - Gmail Compose Mockup Preview */}
        <div className="email-right-section">
          <div className="gmail-mockup">
            <div className="gmail-header">
              <span className="gmail-title">New Message</span>
              <div className="gmail-header-actions">
                <span className="action-btn">─</span>
                <span className="action-btn">⤢</span>
                <span className="action-btn">✕</span>
              </div>
            </div>

            <div className="gmail-recipients">
              <div className="field-row">
                <span className="label">To</span>
                <span className="value text-muted">recipients@example.com</span>
              </div>
              <div className="field-row">
                <span className="label">Subject</span>
                <span className="value subject-val">
                  {subjectText || "Enter template subject"}
                </span>
              </div>
            </div>

            <div className="gmail-body">
              <div className="gmail-content">
                {bodyText ? (
                  <p className="body-text">{bodyText}</p>
                ) : (
                  <span className="body-text text-muted">
                    Type email body on the left to preview...
                  </span>
                )}
              </div>
            </div>

            <div className="gmail-footer">
              <div className="send-btn-group">
                <button className="send-btn" type="button" disabled>
                  Send
                </button>
                <button className="send-dropdown" type="button" disabled>
                  ▾
                </button>
              </div>
              <div className="gmail-toolbar">
                <span className="tool-icon">A</span>
                <span className="tool-icon">
                  <PaperClipOutlined />
                </span>
                <span className="tool-icon">
                  <PictureOutlined />
                </span>
                <span className="tool-icon">🔗</span>
                <span className="tool-icon">☺</span>
                <div style={{ marginLeft: "auto" }}>
                  <span className="tool-icon">
                    <DeleteOutlined />
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Flex>
    </div>
  );
};

export default memo(EmailCreateTemplate);
